import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { api, unwrap, type Schemas } from '../../api/client';
import { useSession, type PracticeSummary } from '../../auth/session';
import { formatAmount, formatDay, type Country } from '@mybooks/practice';
import { APP_NAME } from '../../lib/brand';

// Xone Practice data (ADR 0043). Practice routes act for the person, not a company, so their queries
// are keyed by the practice ('practice', id, ...) rather than by the current company.

export type PracticeClient = Schemas['PracticeClient'];
export type PracticeClientDetail = Schemas['PracticeClientDetail'];
export type Practice = Schemas['Practice'];
export type PracticeMember = Schemas['PracticeMember'];
export type PracticeRole = PracticeSummary['role'];

const CURRENT_KEY = 'mybooks.practice';
function read(): string | null {
  try { return localStorage.getItem(CURRENT_KEY); } catch { return null; }
}
export function rememberPractice(id: string) {
  try { localStorage.setItem(CURRENT_KEY, id); } catch { /* storage blocked */ }
}

/** The practice in use: the one chosen last, else the first. Null for someone in no practice. */
export function useCurrentPractice(): PracticeSummary | null {
  const { practices } = useSession();
  const saved = read();
  return practices.find((p) => p.id === saved) ?? practices[0] ?? null;
}

/** The current practice; only under the practice shell, which renders nothing without one. */
export function usePracticeContext(): PracticeSummary {
  const practice = useCurrentPractice();
  if (!practice) throw new Error('usePracticeContext outside a practice');
  return practice;
}

export const practiceKey = (id: string, ...rest: unknown[]) => ['practice', id, ...rest] as const;

export function usePractice(id: string) {
  return useQuery({
    queryKey: practiceKey(id, 'details'),
    queryFn: async () => (await unwrap(api.GET('/v1/practices/{id}', { params: { path: { id } } }))).data as Practice,
    staleTime: 60_000,
  });
}

export function usePracticeClients(id: string, includeEnded = false) {
  return useQuery({
    queryKey: practiceKey(id, 'clients', includeEnded),
    queryFn: async () =>
      ((await unwrap(api.GET('/v1/practices/{id}/clients', { params: { path: { id }, query: { include_ended: includeEnded ? 'true' : 'false' } } }))).data ?? []) as PracticeClient[],
  });
}

export function usePracticeClient(id: string, linkId: string) {
  return useQuery({
    queryKey: practiceKey(id, 'client', linkId),
    queryFn: async () => (await unwrap(api.GET('/v1/practices/{id}/clients/{linkId}', { params: { path: { id, linkId } } }))).data as PracticeClientDetail,
  });
}

export function usePracticeMembers(id: string) {
  return useQuery({
    queryKey: practiceKey(id, 'members'),
    queryFn: async () => ((await unwrap(api.GET('/v1/practices/{id}/members', { params: { path: { id } } }))).data ?? []) as PracticeMember[],
    staleTime: 60_000,
  });
}

/** Marks a practice's queries stale after a change. */
export function useInvalidatePractice() {
  const queryClient = useQueryClient();
  return useCallback((id: string, ...parts: string[]) =>
    parts.length === 0
      ? queryClient.invalidateQueries({ queryKey: ['practice', id] })
      : Promise.all(parts.map((p) => queryClient.invalidateQueries({ queryKey: ['practice', id, p] }))), [queryClient]);
}

/** Opens a client's books (client books mode) and returns the path to go to. */
export function useOpenClientBooks() {
  const session = useSession();
  const queryClient = useQueryClient();
  return useCallback((practice: PracticeSummary, client: Pick<PracticeClient, 'id' | 'tenant_id' | 'name' | 'state' | 'read_only_after'>) => {
    const readOnly = client.state === 'practice_managed' && !!client.read_only_after && Date.parse(client.read_only_after) <= Date.now();
    queryClient.removeQueries({ predicate: (q) => q.queryKey[0] === client.tenant_id });
    session.openClient({
      tenantId: client.tenant_id as string,
      name: client.name as string,
      practiceId: practice.id,
      practiceName: practice.name,
      linkId: client.id as string,
      role: readOnly ? 'read_only' : 'accountant',
    });
    return '/';
  }, [session, queryClient]);
}

// ---- Words ----

export const PRACTICE_ROLE_NAMES: Record<PracticeRole, string> = { partner: 'Partner', manager: 'Manager', staff: 'Staff' };

export const COMPANY_TYPE_NAMES: Record<string, string> = {
  pty_ltd: '(Pty) Ltd', cc: 'Close corporation', sole_proprietor: 'Sole proprietor', trust: 'Trust', partnership: 'Partnership',
  npc: 'Non-profit company', llc: 'LLC', c_corp: 'C corporation', s_corp: 'S corporation', nonprofit: 'Nonprofit',
};
/** Company types offered per country. */
export const COMPANY_TYPES_FOR: Record<Country, string[]> = {
  ZA: ['pty_ltd', 'cc', 'sole_proprietor', 'trust', 'partnership', 'npc'],
  US: ['llc', 'c_corp', 's_corp', 'sole_proprietor', 'partnership', 'nonprofit'],
};
export const VAT_CATEGORY_NAMES: Record<string, string> = {
  A: 'Two-monthly (A)', B: 'Two-monthly (B)', C: 'Monthly', D: 'Six-monthly', E: 'Annual',
};
const VAT_SHORT: Record<string, string> = { A: 'VAT two-monthly', B: 'VAT two-monthly', C: 'VAT monthly', D: 'VAT six-monthly', E: 'VAT annual' };
const MONTH_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export const monthLong = (m: number) => MONTH_LONG[(m - 1 + 12) % 12] ?? '';

/** "(Pty) Ltd · VAT two-monthly · Year end February": the second line under a client's name. */
export function clientFacts(c: Pick<PracticeClient, 'company_type' | 'vat_registered' | 'vat_category' | 'year_end_month'>): string {
  const parts: string[] = [];
  if (c.company_type) parts.push(COMPANY_TYPE_NAMES[c.company_type] ?? c.company_type);
  parts.push(c.vat_registered && c.vat_category ? (VAT_SHORT[c.vat_category] ?? 'VAT registered') : 'Not VAT registered');
  parts.push(`Year end ${monthLong(c.year_end_month ?? 2)}`);
  return parts.join(' · ');
}

/** What state a client is in, as a dot and a word. */
export function stateOf(c: Pick<PracticeClient, 'state' | 'invited'>): { key: string; label: string } {
  switch (c.state) {
    case 'requested': return { key: 'pending', label: 'Asked for your help' };
    case 'practice_managed': return c.invited ? { key: 'pending', label: 'Invited' } : { key: 'open', label: `Not on ${APP_NAME} yet` };
    case 'ended': return { key: 'void', label: 'Ended' };
    default: return { key: 'done', label: 'Active' };
  }
}

/** The country's money and date formats, bound to a practice. */
export function formatsFor(country: Country) {
  return {
    money: (v: string | bigint | null | undefined, whole = false) => formatAmount(v, country, { whole }),
    day: (iso: string | null | undefined, year = false) => formatDay(iso, country, { year }),
  };
}
